import React, { useEffect, useRef } from 'react'
import { observer } from 'mobx-react-lite'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { ModalProps } from '@/types/modal'
import { useStores } from '@/components/StoreProvider'

const Modal: React.FC<ModalProps> = ({
  id,
  title,
  content,
  size = 'md',
  showClose = true,
  closeOnClickOutside = true,
  onClose,
  footer,
  showFooter = !!footer,
}) => {
  const { chatStore } = useStores()
  const modalRef = useRef<HTMLDivElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)

  const handleClose = () => {
    if (onClose) {
      onClose()
    }
    // Rendered directly (not through a modal registry) the store has nothing to remove.
    ;(chatStore as { removeModal?: (id: string) => void }).removeModal?.(id)
  }

  const handleOutsideClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (closeOnClickOutside && e.target === e.currentTarget) {
      handleClose()
    }
  }

  // Handle keyboard events
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        handleClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Focus trap
  useEffect(() => {
    const modal = modalRef.current
    if (!modal) return

    // Focus the first focusable element
    const focusableElements = modal.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    )

    if (focusableElements.length > 0) {
      ;(focusableElements[0] as HTMLElement).focus()
    } else {
      // If no focusable elements, focus the modal itself
      modal.focus()
    }
  }, [])

  // Modal sizes object
  const modalSizes = {
    sm: 'w-full max-w-sm',
    md: 'w-full max-w-md',
    lg: 'w-full max-w-lg',
    xl: 'w-full max-w-xl',
    full: 'w-full max-w-4xl',
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center p-4 animate-in fade-in duration-200">
      <div
        onClick={handleOutsideClick}
        className="fixed inset-0 flex items-center justify-center p-4 animate-in fade-in duration-200"
        style={{ zIndex: 0, backgroundColor: 'black', opacity: 0.2 }}
      ></div>
      <div
        ref={modalRef}
        className={cn(
          'relative bg-white rounded-lg shadow-lg overflow-hidden animate-in zoom-in-95 duration-150',
          modalSizes[size],
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`modal-title-${id}`}
        tabIndex={-1}
      >
        {/* Header */}
        <div
          ref={headerRef}
          className="p-4 sm:p-6 border-b border-gray-200 flex items-center justify-between"
        >
          <h2
            id={`modal-title-${id}`}
            className="text-xl font-semibold text-gray-800"
          >
            {title}
          </h2>
          {showClose && (
            <button
              onClick={handleClose}
              className="p-1 rounded-full hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300"
              aria-label="Close modal"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(80vh-12rem)]">
          {content}
        </div>

        {/* Footer */}
        {showFooter && (
          <div className="p-4 sm:p-6 border-t border-gray-200 flex items-center justify-end gap-3 bg-gray-50">
            {footer}
          </div>
        )}
      </div>
    </div>
  )
}

export default observer(Modal)
